import type { SettingsProps, TicketField, ZAFClient } from "./interface";
import type { SelectedProduct } from "../../pages/NewOrder/interfaces";

// Interface para resposta de criação de ticket
interface CreateTicketResponse {
  ticket: {
    id: number;
    url: string;
    subject: string;
    status: string;
  };
}

// Interface para resposta de criação de side conversation
interface CreateSideConversationResponse {
  side_conversation: {
    id: string;
    subject: string;
    created_at: string;
    updated_at: string;
    state: string;
  };
}

// Interface para resposta de criação de incident com child ticket ID
interface CreateIncidentResponse extends CreateTicketResponse {
  childTicketId?: number;
}

// TODO: COLOCAR TODAS AS INTERFACES DESSE ARQUIVO EM UM ÚNICO LUGAR

declare global {
  interface Window {
    zafClient: ZAFClient;
    initializedClient?: { _origin: string };
  }
}
export let zafClient = (window as Window).zafClient;

export const loadZendeskSdk = async (): Promise<void> => {
  if (window.zafClient) {
    zafClient = window.zafClient;
    return;
  }

  // Aguardar o SDK estar disponível (já que está sendo carregado via HTML)
  await new Promise<void>((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo

    const checkSdk = () => {
      if ((window as any).ZAFClient) {
        try {
          window.zafClient = (
            window as typeof window & {
              ZAFClient: { init: () => unknown };
            }
          ).ZAFClient.init() as unknown as ZAFClient;
          zafClient = window.zafClient!;
          resolve();
        } catch (error) {
          reject(new Error("Erro ao inicializar ZAFClient: " + error));
        }
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkSdk, 100);
      } else {
        reject(new Error("Timeout: Zendesk SDK não carregou a tempo"));
      }
    };

    checkSdk();
  });
};

export const initializeZafClient = async (): Promise<void> => {
  console.log("🔧 DEBUG: Iniciando inicialização do zafClient");
  
  // Se já temos um cliente válido, retorna
  if (zafClient && typeof zafClient.invoke === "function") {
    console.log("✅ DEBUG: zafClient já está inicializado");
    return;
  }

  try {
    console.log("🔧 DEBUG: Carregando Zendesk SDK...");
    await loadZendeskSdk();

    if (!window.zafClient) {
      throw new Error("Zendesk client não encontrado após carregar o SDK.");
    }

    zafClient = window.zafClient;

    // Verificar se o cliente foi inicializado corretamente
    if (!zafClient || typeof zafClient.invoke !== "function") {
      throw new Error("Zendesk client não foi inicializado corretamente.");
    }
    
    console.log("✅ DEBUG: zafClient inicializado com sucesso");
    console.log("🔧 DEBUG: zafClient.invoke disponível:", typeof zafClient.invoke);
    console.log("🔧 DEBUG: zafClient.request disponível:", typeof zafClient.request);
  } catch (error) {
    console.error("❌ Erro ao inicializar Zendesk client:", error);
    throw error;
  }
};

export const getApiUrl = async () => {
  const client = zafClient;
  const metadata = await client.metadata();

  const correiosUser = metadata.settings?.["correios-user"] as string;
  const correiosPassword = metadata.settings?.["correios-password"] as string;
  const correiosUrl = metadata.settings?.["correios-url"] as string;

  return {
    correiosUrl,
    correiosUser,
    correiosPassword,
  };
};

export const getMetadata = async () => {
  const client = zafClient;
  const metadata = await client.metadata();
  const metadataContent: SettingsProps = {
    "correios-user": metadata.settings["correios-user"],
    "correios-password": metadata.settings["correios-password"],
    "correios-url": metadata.settings["correios-url"],
  };

  return metadataContent;
};

export function popupError(message: string, sticky: boolean = false) {
  if (zafClient && typeof zafClient.invoke === "function") {
    zafClient.invoke("notify", message, "error", { sticky });
  } else {
    console.error("Zendesk Error:", message);
    // Fallback para alert se o zafClient não estiver disponível
    alert(`Erro: ${message}`);
  }
}

export function popupSuccess(message: string, sticky: boolean = false) {
  if (zafClient && typeof zafClient.invoke === "function") {
    zafClient.invoke("notify", message, "success", { sticky });
  } else {
    // Fallback para alert se o zafClient não estiver disponível
    alert(`Sucesso: ${message}`);
  }
}

let cachedTicketFields: TicketField[] | null = null;

export async function getAllTicketFields() {
  if (cachedTicketFields) return cachedTicketFields;

  const url = "/api/v2/ticket_fields";
  const resp = await zafClient.request({
    type: "GET",
    url,
  });

  cachedTicketFields = (resp as { ticket_fields: TicketField[] })[
    "ticket_fields"
  ];
  return cachedTicketFields;
}

export async function getTicketFieldValue(fieldId: number) {
  const fieldName = `custom_field_${fieldId}`;
  const response = await zafClient.get(`ticket.customField:${fieldName}`);
  return response[`ticket.customField:${fieldName}`];
}

export async function getCurrentTicketNumber(zafClient: ZAFClient) {
  try {
    const ticket = await zafClient.get("ticket");
    return (ticket as unknown as { ticket: { id: number } }).ticket.id;
  } catch (error) {
    console.error("Error getting ticket number:", error);
    return null;
  }
}

type FieldsResponse = TicketField;

interface TicketForm {
  id: number;
  name: string;
  display_name: string;
  ticket_field_ids: number[];
}

async function getFormFieldsData(
  zafClient: ZAFClient,
  form: TicketForm,
  customFields: Record<number, unknown> | Array<{ id: number; value: unknown }>,
  allTicketFields: FieldsResponse[],
  isCurrentForm: boolean = false
) {
  const formFields = allTicketFields.filter((field: FieldsResponse) =>
    form.ticket_field_ids.includes(field.id)
  );

  const excludedFieldTypes = [
    "subject",
    "description",
    "status",
    "group",
    "assignee",
    "custom_status",
  ];

  const fieldList: Array<{
    id: number;
    name: string;
    type: string;
    value: string;
    display_value: string;
    form_id: number;
  } | null> = await Promise.all(
    formFields.map(async (field: FieldsResponse) => {
      if (excludedFieldTypes.includes(field.type)) return null;

      const fieldValue = Array.isArray(customFields)
        ? customFields.find(
            (f: { id: number; value: unknown }) => f.id === field.id
          )?.value ?? null
        : customFields[field.id] ?? null;

      let displayValue = fieldValue;
      if (field.type === "tagger" && fieldValue) {
        displayValue = await getTaggerDisplayValue(
          zafClient,
          field.id,
          fieldValue
        );
      }

      return {
        id: field.id,
        name: field.title || field.raw_title,
        type: field.type,
        value: fieldValue,
        display_value: displayValue,
        form_id: form.id,
      };
    })
  );

  return {
    form: {
      id: form.id,
      name: form.name,
      display_name: form.display_name,
      current: isCurrentForm,
      ticketNumber: isCurrentForm
        ? await getCurrentTicketNumber(zafClient)
        : undefined,
    },
    fields: fieldList.filter(Boolean),
  };
}

interface CustomFieldOption {
  id: number;
  name: string;
  value: string;
}

async function getTaggerDisplayValue(
  zafClient: ZAFClient,
  fieldId: number,
  fieldValue: string
) {
  try {
    const taggerFieldResponse = (await zafClient.request({
      url: `/api/v2/ticket_fields/${fieldId}`,
      type: "GET",
    })) as { ticket_field: { custom_field_options?: CustomFieldOption[] } };

    const tagOption =
      taggerFieldResponse.ticket_field.custom_field_options?.find(
        (opt: CustomFieldOption) => opt.value === fieldValue
      );

    return tagOption?.name ?? fieldValue;
  } catch (error) {
    console.error(`Erro ao buscar opções do campo tagger ${fieldId}:`, error);
    return fieldValue;
  }
}

interface AuditEvent {
  type: string;
  field_name?: string;
  value?: string | number;
}

interface Audit {
  events?: AuditEvent[];
}

async function getHistoricalFormIds(
  zafClient: ZAFClient,
  ticketNumber: number
) {
  const auditsResponse = (await zafClient.request({
    url: `/api/v2/tickets/${ticketNumber}/audits`,
    type: "GET",
  })) as { audits: Audit[] };
  const formIds = new Set<number>();

  auditsResponse.audits.forEach((audit: Audit) => {
    audit.events?.forEach((event: AuditEvent) => {
      if (
        event.type === "Change" &&
        event.field_name === "ticket_form_id" &&
        event.value
      ) {
        formIds.add(Number(event.value));
      }
    });
  });

  return Array.from(formIds);
}

export async function getAllTicketFormsData(
  zafClient: ZAFClient,
  ticketNumber: number
) {
  try {
    const ticketResponse = (await zafClient.request({
      url: `/api/v2/tickets/${ticketNumber}?include=form`,
      type: "GET",
    })) as {
      ticket: {
        ticket_form_id: number;
        custom_fields?: Record<string, unknown>;
      };
    };

    const currentFormId = ticketResponse.ticket.ticket_form_id;
    if (!currentFormId) {
      popupError("Ticket não possui formulário associado.", false);
      return null;
    }

    const historicalFormIds = await getHistoricalFormIds(
      zafClient,
      ticketNumber
    );
    const allFormIds = [...new Set([currentFormId, ...historicalFormIds])];

    const formsResponse = (await zafClient.request({
      url: `/api/v2/ticket_forms?ids=${allFormIds.join(",")}`,
      type: "GET",
    })) as { ticket_forms: TicketForm[] };

    const fieldsResponse = (await zafClient.request({
      url: `/api/v2/ticket_fields`,
      type: "GET",
    })) as { ticket_fields: FieldsResponse[] };

    const allFormsData = await Promise.all(
      formsResponse.ticket_forms.map(async (form: TicketForm) => {
        return await getFormFieldsData(
          zafClient,
          form,
          ticketResponse.ticket.custom_fields || {},
          fieldsResponse.ticket_fields,
          form.id === currentFormId
        );
      })
    );

    return allFormsData;
  } catch (error) {
    handleFormError(error);
    return null;
  }
}

function handleFormError(error: unknown) {
  if (error instanceof Error) {
    popupError(error.message, false);
  } else {
    popupError("Erro desconhecido ao processar formulários.", false);
  }
}

export async function addInternalComment(message: string): Promise<void> {
  try {
    const ticketData = await zafClient.get("ticket");
    const ticketId = (ticketData as unknown as { ticket: { id: number } })
      .ticket.id;

    await zafClient.request({
      url: `/api/v2/tickets/${ticketId}.json`,
      type: "PUT",
      data: {
        ticket: {
          comment: {
            body: message,
            public: false,
          },
        },
      },
    });

    popupSuccess("Comentário interno adicionado com sucesso!", false);
  } catch (error) {
    console.error("Erro ao adicionar comentário interno:", error);
    popupError("Erro ao adicionar comentário interno.", false);
  }
}

export interface IZendeskCurrentUser {
  alias: string;
  avatarUrl: string;
  details: string;
  email: string;
  externalId: string | null;
  id: number;
  identities: unknown[];
  isSystemUser: boolean;
  locale: string;
  name: string;
  notes: string;
  organizations: unknown[];
  role: string;
  signature: string;
  tags: string[];
  timeZone: {
    name: string;
    translatedName: string;
    ianaName: string;
    offset: number;
    formattedOffset: string;
  };
  groups: {
    id: number;
    name: string;
  }[];
}

export async function getCurrentUserName() {
  try {
    const { currentUser } = await zafClient.get("currentUser");
    return currentUser as IZendeskCurrentUser;
  } catch (error) {
    console.error("Erro ao obter usuário atual do Zendesk:", error);
    return {
      alias: "",
      avatarUrl: "",
      details: "",
      email: "",
      externalId: null,
      id: 0,
      identities: [],
      isSystemUser: false,
      locale: "",
      name: "Usuário desconhecido",
      notes: "",
      organizations: [],
      role: "",
      signature: "",
      tags: [],
      timeZone: {
        name: "",
        translatedName: "",
        ianaName: "",
        offset: 0,
        formattedOffset: "",
      },
      groups: [],
    };
  }
}

// Interface para os dados do solicitante do ticket
export interface RequesterData {
  id: number;
  name: string;
  email: string;
  phone?: string;
  user_fields?: Record<string, unknown>;
}

export async function getRequesterData(): Promise<RequesterData | null> {
  try {
    const zafResponse = await zafClient.get("ticket.requester");
    const zafRequester = zafResponse["ticket.requester"] as {
      id?: number;
      name?: string;
      email?: string;
    } | null;

    if (!zafRequester?.id) {
      console.warn("⚠️ getRequesterData: ticket.requester sem ID via ZAF");
      return null;
    }

    const requesterId = zafRequester.id;
    console.log("🔧 DEBUG: getRequesterData - ID do requester via ZAF:", requesterId);

    const apiResponse = await zafClient.request({
      url: `/api/v2/users/${requesterId}.json`,
      type: "GET",
    }) as { user: RequesterData };

    const user = apiResponse.user;

    console.log("✅ DEBUG: getRequesterData - dados completos do requester:", {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      user_fields: user.user_fields,
    });

    return {
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      user_fields: user.user_fields,
    };
  } catch (error) {
    console.error("❌ getRequesterData: erro ao buscar dados do requester:", error);
    return null;
  }
}

export async function getInternalComments(zafClient: any) {
  const ticketData = await zafClient.get("ticket");
  const ticketId = (ticketData as unknown as { ticket: { id: number } }).ticket
    .id;

  const res = await zafClient.request({
    url: `/api/v2/tickets/${ticketId}/comments.json?sort_order=desc`,
    type: "GET",
  });

  const internos = (res.comments || []).filter((c: any) => c.public === false);

  if (!internos.length) return null;

  const lastComment = internos[0];
  const match = lastComment.body.match(/área\s+(.+?),\s*usuário/i);
  const area = match ? match[1].trim() : null;

  return {
    ...lastComment,
    area,
  };
}

export const getZendeskUrl = () => {
  const zendeskUrl =
    (window.location.ancestorOrigins === undefined
      ? (window as Window).initializedClient?._origin
      : window.location.ancestorOrigins[0]) || "";

  return zendeskUrl;
};

// type TicketShow = {
//   ticket: { id: number; type?: string; brand_id?: number; problem_id?: number };
// };

// async function getCurrentTicketId(): Promise<number> {
//   const d = await zafClient.get('ticket.id');
//   return Number(d['ticket.id']);
// }

// async function fetchTicket(id: number) {
//   return (await zafClient.request({
//     url: `/api/v2/tickets/${id}.json`,
//     type: 'GET',
//   })) as TicketShow;
// }

// export async function resolveProblemAndBrand(mainTicketId?: number | string): Promise<{
//   problemId?: number;
//   brandIdToUse?: number;
// }> {
//   const mainId = Number(mainTicketId);
//   if (mainTicketId && Number.isFinite(mainId)) {
//     try {
//       const r = await fetchTicket(mainId);
//       if (r.ticket?.type === 'problem') {
//         return { problemId: r.ticket.id, brandIdToUse: r.ticket.brand_id };
//       }
//     } catch {}
//   }

//   const currentId = await getCurrentTicketId();
//   const cur = await fetchTicket(currentId);

//   if (cur.ticket?.type === 'problem') {
//     return { problemId: cur.ticket.id, brandIdToUse: cur.ticket.brand_id };
//   }

//   if (cur.ticket?.problem_id) {
//     try {
//       const prob = await fetchTicket(cur.ticket.problem_id);
//       if (prob.ticket?.type === 'problem') {
//         return { problemId: prob.ticket.id, brandIdToUse: prob.ticket.brand_id };
//       }
//     } catch {}
//   }

//   return {};
// }

interface ICreateTicket {
  codigoObjeto: string;
  tipoPostagem?: string;
  servico: string;
  // tipoRegistro: string;
  valorProduto: string;
  postagemReversa: boolean;
  codigoDevolucao?: string;
  motivoDevolucao?: string;
  // dataSolicitacao: string;
  prazoCorreios: string;
  rastreioDevolucao: string;
  statusPostagem: string;
  observacaoColeta?: string;
  notaFical?: string;
  estabelecimento?: string;
  serie: string;
  produto: string;
  tipoEnvioPostagem?: string;
}

interface ICreateShipmentTicket {
  body: ICreateTicket;
  description: string;
  mainTicketId?: number | string;
  requesterId: number;
  productName?: string;
  agentId: number;
}

// ==== CONSTS (ajuste se necessário) ====
const FORM_ID = 38372911845396;
const FIELD_COD_RASTREIO = 38372417820436;
const FIELD_LOOKUP_PRODUTO = 38546069276180;

// ==== HELPERS DE DATA ====
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const brToIsoDate = (s: string) => {
  const [dd, mm, yyyy] = (s ?? "").split("/");
  if (!dd || !mm || !yyyy) return "";
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
};

// Função para formatar data para YYYY-MM-DD
function formatDateForZendesk(dateString: string): string {
  if (!dateString) return dateString;

  try {
    // Se já está no formato YYYY-MM-DD, retorna como está
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Se está no formato DD/MM/YYYY, converte para YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split("/");
      return `${year}-${month}-${day}`;
    }

    // Tenta parsear como Date e converter
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }

    return dateString;
  } catch (error) {
    console.warn(`Erro ao formatar data ${dateString}:`, error);
    return dateString;
  }
}

// ==== RESOLVE PROBLEM + BRAND (mantém sua lógica) ====
type TicketShow = {
  ticket: { id: number; type?: string; brand_id?: number; problem_id?: number };
};

async function fetchTicket(id: number) {
  return (await zafClient.request({
    url: `/api/v2/tickets/${id}.json`,
    type: "GET",
  })) as TicketShow;
}
export async function resolveProblemAndBrand(
  mainTicketId?: number | string
): Promise<{
  problemId?: number;
  brandIdToUse?: number;
  brandName?: string;
}> {
  const getBrandName = async (brandId?: number) => {
    if (!brandId) return undefined;
    try {
      const r = await zafClient.request({
        url: `/api/v2/brands/${brandId}.json`,
        type: "GET",
      });
      return (r as any).brand?.name as string | undefined;
    } catch {
      return undefined;
    }
  };

  const mainId = Number(mainTicketId);
  if (mainTicketId && Number.isFinite(mainId)) {
    try {
      const r = await fetchTicket(mainId);
      if (r.ticket?.type === "problem") {
        return {
          problemId: r.ticket.id,
          brandIdToUse: r.ticket.brand_id,
          brandName: await getBrandName(r.ticket.brand_id),
        };
      }
    } catch {}
  }

  const currentId = await getCurrentTicketId();
  const cur = await fetchTicket(currentId);

  if (cur.ticket?.type === "problem") {
    return {
      problemId: cur.ticket.id,
      brandIdToUse: cur.ticket.brand_id,
      brandName: await getBrandName(cur.ticket.brand_id),
    };
  }

  if (cur.ticket?.problem_id) {
    try {
      const prob = await fetchTicket(cur.ticket.problem_id);
      if (prob.ticket?.type === "problem") {
        return {
          problemId: prob.ticket.id,
          brandIdToUse: prob.ticket.brand_id,
          brandName: await getBrandName(prob.ticket.brand_id),
        };
      }
    } catch {}
  }

  return {};
}

type TicketFieldShow = { ticket_field: { relationship_target_type?: string } };
type CORecord = {
  id: string;
  external_id?: string;
  properties?: Record<string, any>;
  name?: string;
};
type CORecordsSearchGET = { records?: CORecord[] };

async function getCurrentLookupRecordId(
  fieldId: number
): Promise<string | undefined> {
  const key = `ticket.customField:custom_field_${fieldId}`;
  const data = await zafClient.get(key);
  const raw = (data as any)[key];
  return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}

async function getLookupTargetKey(fieldId: number) {
  const r = (await zafClient.request({
    url: `/api/v2/ticket_fields/${fieldId}.json`,
    type: "GET",
  })) as TicketFieldShow;
  const rtt = r.ticket_field.relationship_target_type || "";
  const key = rtt.startsWith("zen:custom_object:")
    ? rtt.split(":").pop()
    : undefined;
  if (!key) throw new Error("Campo lookup sem relationship_target_type.");
  return key;
}

async function findCORecordIdByValue(objectKey: string, text: string) {
  const v = (text ?? "").trim();
  if (!v) return undefined;

  const q1 = encodeURIComponent(`custom_object_key:${objectKey} name:"${v}"`);
  let res = (await zafClient.request({
    url: `/api/v2/custom_objects/records/search?query=${q1}`,
    type: "GET",
  })) as CORecordsSearchGET;
  if (res.records?.[0]?.id) return res.records[0].id;

  const props = [
    "sku",
    "produto_comercial",
    "referencia_telecontrol",
    "codigo_erp",
    "codigo",
    "modelo",
  ];
  for (const p of props) {
    const q = encodeURIComponent(
      `custom_object_key:${objectKey} properties.${p}:"${v}"`
    );
    res = (await zafClient.request({
      url: `/api/v2/custom_objects/records/search?query=${q}`,
      type: "GET",
    })) as CORecordsSearchGET;
    if (res.records?.[0]?.id) return res.records[0].id;
  }
  return undefined;
}

async function resolveProductLookupId(fieldId: number, productText?: string) {
  const fromCurrent = await getCurrentLookupRecordId(fieldId);
  if (fromCurrent) return fromCurrent;

  const key = await getLookupTargetKey(fieldId);
  return findCORecordIdByValue(key, productText ?? "");
}

export async function createTicket({
  body,
  description,
  mainTicketId,
  requesterId,
  productName,
  agentId,
}: ICreateShipmentTicket) {
  if (!body.codigoObjeto) throw new Error("codigoObjeto vazio");

  const { problemId, brandIdToUse } = await resolveProblemAndBrand(
    mainTicketId
  );

  const productRecordId = await resolveProductLookupId(
    FIELD_LOOKUP_PRODUTO,
    body.produto ?? productName
  );

  const custom_fields: Array<{ id: number; value: any }> = [
    { id: FIELD_COD_RASTREIO, value: body.codigoObjeto },
    {
      id: 38378192293268,
      value:
        body.tipoPostagem === "A"
          ? "a___autorização_de_postagem"
          : body.tipoPostagem === "CA"
          ? "ca___coleta_domiciliar"
          : "",
    },
    { id: 38375455350420, value: body.servico === "03298" ? "pac" : "sedex" },
    { id: 38373007208340, value: "correios_lgr_layout" },
    { id: 38375898555796, value: body.valorProduto },
    { id: 38375377664148, value: body.postagemReversa ? true : false },
    { id: 38374263613844, value: body.codigoDevolucao },
    { id: 38375149016980, value: body.motivoDevolucao },
    { id: 38374386331796, value: toIsoDate(new Date()) },
    { id: 38375383498260, value: brToIsoDate(body.prazoCorreios) },
    { id: 38375408016788, value: body.rastreioDevolucao },
    { id: 38375718268692, value: body.statusPostagem },
    { id: 38377758666900, value: body.observacaoColeta },
    { id: 38375250215188, value: body.notaFical },
    { id: 38374638222996, value: body.estabelecimento },
    { id: 38375412352532, value: body.serie },
    {
      id: 38379663225748,
      value: body.tipoEnvioPostagem,
    },
    ...(productRecordId
      ? [{ id: FIELD_LOOKUP_PRODUTO, value: productRecordId }]
      : []),
  ];

  const payload = {
    ticket: {
      subject: `LGR ${productName}`,
      type: "incident",
      requester_id: requesterId,
      priority: "low",
      ticket_form_id: FORM_ID,
      ...(brandIdToUse ? { brand_id: brandIdToUse } : {}),
      ...(problemId ? { problem_id: problemId } : {}),
      comment: {
        body: description,
        public: false,
        author_id: agentId, // 👈 aqui entra o ID que você já tem
      },

      custom_fields,
    },
  };

  const res = await (zafClient.request as any)({
    url: "/api/v2/tickets.json",
    type: "POST",
    contentType: "application/json",
    data: JSON.stringify(payload),
  });

  return res as CreateTicketResponse;
}

// Interfaces para Side Conversations
interface SideConversationParticipant {
  email?: string;
  name?: string;
  user_id?: number;
}

interface SideConversation {
  id: string;
  subject: string;
  state: "open" | "closed";
  created_at: string;
  updated_at: string;
  message_added_at: string;
  participants: SideConversationParticipant[];
  preview_text: string;
  ticket_id: number;
  url: string;
  external_ids?: Record<string, string>;
}

// Interface para Side Conversation
export interface SideConversationData {
  id: string;
  subject: string;
  created_at: string;
  updated_at: string;
  participants: Array<{
    user_id: number;
    name?: string;
  }>;
  message_count: number;
  state: string;
  preview: {
    text: string;
  };
}

// Interface para resposta da API de Side Conversations
interface SideConversationsResponse {
  side_conversations: SideConversationData[];
  next_page: string | null;
  previous_page: string | null;
  count: number;
}

// Interface para mensagens/eventos da side conversation
interface SideConversationEventsResponse {
  events?: Array<{
    id: string;
    type: string;
    body?: string;
    message?: string;
    created_at: string;
  }>;
  messages?: Array<{
    id: string;
    body: string;
    created_at: string;
  }>;
}

// Função para buscar side conversations de um ticket
export async function getSideConversations(
  ticketId: number
): Promise<SideConversationData[]> {
  try {
    const response = await zafClient.request({
      url: `/api/v2/tickets/${ticketId}/side_conversations`,
      type: "GET",
    });

    const sideConversations =
      (response as SideConversationsResponse).side_conversations || [];

    // Filtrar apenas side conversations relacionadas a pedidos (que contêm "Novo Pedido" no subject)
    // E que estejam abertas (state = "open")
    const pedidoSideConversations = sideConversations.filter(
      (sc) =>
        sc.subject && sc.subject.includes("Novo Pedido") && sc.state === "open"
    ); // Log para debug: mostrar estados das side conversations
    sideConversations.forEach((sc) => {
      if (sc.subject && sc.subject.includes("Novo Pedido")) {
        console.log(
          `📋 Side conversation encontrada: ${sc.subject} - Estado: ${sc.state}`
        );
      }
    });

    return pedidoSideConversations;
  } catch (error) {
    console.error("❌ Erro ao buscar side conversations:", error);
    throw error;
  }
}

// Função para buscar detalhes completos de uma side conversation, incluindo external_ids
export async function getSideConversationWithExternalIds(
  ticketId: number,
  sideConversationId: string
): Promise<SideConversation | null> {
  try {
    const response = await zafClient.request({
      url: `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}`,
      type: "GET",
    });

    const sideConversation = (
      response as { side_conversation?: SideConversation }
    ).side_conversation;

    if (sideConversation) {
      console.log(`✅ Side conversation encontrada:`, {
        id: sideConversation.id,
        subject: sideConversation.subject,
        external_ids: sideConversation.external_ids,
      });

      // Se tem targetTicketId, é um "child ticket"
      if (sideConversation.external_ids?.targetTicketId) {
        console.log(
          `✅ Encontrado child ticket ID: ${sideConversation.external_ids.targetTicketId}`
        );
      }
    }

    return sideConversation || null;
  } catch (error) {
    console.error(
      `❌ Erro ao buscar detalhes da side conversation ${sideConversationId}:`,
      error
    );
    return null;
  }
}

// Função para buscar detalhes de uma side conversation específica
export async function getSideConversationDetails(
  ticketId: number,
  sideConversationId: string
): Promise<any> {
  try {
    // Tentar buscar as mensagens da side conversation
    const response = await zafClient.request({
      url: `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}`,
      type: "GET",
    });

    // Tentar buscar as mensagens separadamente se não vieram no response principal
    try {
      const messagesResponse = await zafClient.request({
        url: `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}/events`,
        type: "GET",
      });

      // Combinar os dados
      const typedMessagesResponse =
        messagesResponse as SideConversationEventsResponse;
      return {
        ...(response as object),
        messages:
          typedMessagesResponse?.events ||
          typedMessagesResponse?.messages ||
          [],
      };
    } catch {
      return response;
    }
  } catch (error) {
    console.error("❌ Erro ao buscar detalhes da side conversation:", error);
    throw error;
  }
}

// Interface para dados do formulário de novo pedido
export interface NewOrderFormData {
  // Campos novos da side conversation
  solicitante: string;
  valorFrete?: string;
  canalVenda?: string;
  tipoDoTicket: string;
  categoriaTicket: string;
  codigoRastreio?: string;
  errorIntegracao?: string;
  valorPedido?: string;

  // Campos existentes
  estabelecimento: string;
  tipoEnvio?: string;
  pedidoInterno: string;
  clonarPedido?: boolean;
  classificacaoTroca: string;
  avaliacaoCredito?: string;
  plataforma?: string;
  retira?: string;
  numeroParcelas?: number;
  ecomPj?: boolean;
  observacoes?: string;
  evidenciaMotivo?: string;
  statusIntegracao: string;
  tipoRegistro: string;
  dataEntrega?: string;
  dataIntegracao: string;
}

// Mapeamento dos IDs dos campos do Zendesk para Side Conversations
// TODO: trocar id do campo por chave do campo na hora de salvar
export const ZENDESK_FIELD_IDS = {
  // ===== NOVOS CAMPOS - Side Conversation =====
  estabelecimento: 39037414513044,
  tipoEnvio: 38483041903892,
  pedidoInterno1: 38338187218068, // Pedido interno - campo 1
  pedidoInterno2: 38958525136020, // Pedido interno - campo 2
  classificacaoTroca: 38484209448340,
  avaliacaoCredito: 38479934791700,
  plataforma: 38485790240276,
  retira: 38485876479380,
  numeroParcelas: 38481300258324,
  ecomPj: 38486427082004,
  observacoes: 38924167327892,
  evidenciaMotivo: 38972433538964,
  statusIntegracao: 38482752257556,
  statusIntegracaoTwo: 41144358653460,
  tipoRegistro: 38802821703316,
  dataEntrega: 38484953319828,
  dataIntegracao: 38480876560020,  ticket: 38802991392916,
  valorFrete: 38486397773076,
  canalVenda: 38479982957332,
  tipoDoTicket: 43425410874900, // Novo ID do campo tipo do ticket
  
  // Campo categoria do ticket na side conversation (ID atualizado)
  categoriaTicket: 43427367580564, // Novo ID do campo categoria

  // Categoria do ticket baseada na marca (campos de origem - leitura)
  categoriaTicketSacAt: 37665983385876, // [SAC AT] Tipo
  categoriaTicketSacEcom: 37667492934292, // [SAC ECOMM] Categoria
  categoriaTicketJuridico: 37665002261652, // [Jurídico] Categoria
  categoriaTicketDefault: 43239028425620, // Campo padrão antigo

  valorPedido: 38483107903892,
  valorPedidoProblem: 43524105964820, // Campo valor pedido na side conversation problem
  clonarPedido: 43426335197588, // Campo "Clonar pedido"

  // ===== CAMPOS - Formulário Itens do pedido (42958690377492) =====
  quantidade: 40834654892436, // [Produto] Quantidade
  preco: 42958750996628, // Preço
  produtoObjeto: 38546069276180, // Produto (objeto)
  tipo: 30150009725972, // Tipo (sempre "incidente")
} as const;

export async function createSideConversation(
  ticketId: number,
  formData: NewOrderFormData,
  products: SelectedProduct[] = [],
  opts?: {
    brandId?: number | string;
    groupIdForChild?: number | string;
    requesterId?: number; // ID do solicitante selecionado
    solicitanteNome?: string; // Nome do solicitante selecionado
  }
): Promise<CreateSideConversationResponse> {
  const currentUser = await getCurrentUserName();

  // Usar o solicitante selecionado ou fallback para o usuário atual
  const effectiveRequesterId = opts?.requesterId || Number(currentUser.id);

  // Calcular o valor total do pedido baseado nos produtos
  const valorTotalPedido = calculateOrderTotal(products);

  // Gerar ID único do pedido baseado no índice
  let pedidoId = formData.pedidoInterno;

  // Se o pedido interno for "teste" ou estiver vazio, gerar automaticamente
  if (!pedidoId || pedidoId.toLowerCase() === "teste") {
    try {
      // Buscar side conversations existentes para contar
      const existingSideConversations = await getSideConversations(ticketId);
      const nextIndex = existingSideConversations.length + 1;
      pedidoId = `P${String(nextIndex).padStart(3, "0")}`; // Ex: P001, P002, P003
      console.log(`🔧 DEBUG: Gerado ID automático do pedido: ${pedidoId}`);
    } catch {
      // Fallback: usar timestamp
      const timestamp = new Date().getTime().toString().slice(-6);
      pedidoId = `P${timestamp}`;
      console.log(`🔧 DEBUG: Gerado ID de fallback: ${pedidoId}`);
    }
  }

  const messageBody = buildSideConversationMessage(
    formData,
    products,
    valorTotalPedido,
    pedidoId,
    opts?.solicitanteNome
  );

  const customFields = [
    // Campos novos da side conversation
    { id: ZENDESK_FIELD_IDS.estabelecimento, value: formData.estabelecimento },
    { id: ZENDESK_FIELD_IDS.tipoEnvio, value: formData.tipoEnvio },
    { id: ZENDESK_FIELD_IDS.pedidoInterno1, value: formData.pedidoInterno },
    { id: ZENDESK_FIELD_IDS.pedidoInterno2, value: formData.pedidoInterno },
    {
      id: ZENDESK_FIELD_IDS.classificacaoTroca,
      value: formData.classificacaoTroca,
    },
    {
      id: ZENDESK_FIELD_IDS.avaliacaoCredito,
      value: formData.avaliacaoCredito,
    },
    { id: ZENDESK_FIELD_IDS.plataforma, value: formData.plataforma },
    { id: ZENDESK_FIELD_IDS.retira, value: formData.retira },
    { id: ZENDESK_FIELD_IDS.numeroParcelas, value: formData.numeroParcelas },
    { id: ZENDESK_FIELD_IDS.ecomPj, value: formData.ecomPj },
    {
      id: ZENDESK_FIELD_IDS.statusIntegracao,
      value: "pedido_status_da_integracao_pendente",
    },
    {
      id: ZENDESK_FIELD_IDS.statusIntegracaoTwo,
      value: "pendente_implantação",
    },
    { id: ZENDESK_FIELD_IDS.tipoRegistro, value: formData.tipoRegistro },
    {
      id: ZENDESK_FIELD_IDS.dataEntrega,
      value: formData.dataEntrega
        ? formatDateForZendesk(formData.dataEntrega)
        : null,
    },
    {
      id: ZENDESK_FIELD_IDS.dataIntegracao,
      value: formatDateForZendesk(formData.dataIntegracao),
    },
    { id: ZENDESK_FIELD_IDS.ticket, value: String(ticketId) },
    { id: ZENDESK_FIELD_IDS.valorFrete, value: formData.valorFrete },
    { id: ZENDESK_FIELD_IDS.canalVenda, value: formData.canalVenda },
    // Tipo do ticket (agora com ID correto)
    { id: ZENDESK_FIELD_IDS.tipoDoTicket, value: formData.tipoDoTicket },
    // Categoria do ticket (usando novo ID único)
    {
      id: ZENDESK_FIELD_IDS.categoriaTicket,
      value: formData.categoriaTicket,
    },
    // Usar valor calculado dos produtos em vez do formData
    { id: ZENDESK_FIELD_IDS.valorPedido, value: valorTotalPedido.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) },
    // Campo valor pedido para side conversation problem (regex: ^\d+\.\d{2}$)
    { id: ZENDESK_FIELD_IDS.valorPedidoProblem, value: valorTotalPedido.toFixed(2) },
    // Usar o pedido ID gerado ou original
    { id: ZENDESK_FIELD_IDS.pedidoInterno1, value: pedidoId },
    { id: ZENDESK_FIELD_IDS.pedidoInterno2, value: pedidoId },
    // Campo "Clonar pedido"
    { id: ZENDESK_FIELD_IDS.clonarPedido, value: formData.clonarPedido },

    // Campos opcionais
    ...(formData.observacoes
      ? [{ id: ZENDESK_FIELD_IDS.observacoes, value: formData.observacoes }]
      : []),
    ...(formData.evidenciaMotivo
      ? [
          {
            id: ZENDESK_FIELD_IDS.evidenciaMotivo,
            value: formData.evidenciaMotivo,
          },
        ]
      : []),
  ].filter((f) => f.value !== undefined && f.value !== "");

  console.log(`🔧 DEBUG: Adicionando campo valorPedidoProblem (ID: ${ZENDESK_FIELD_IDS.valorPedidoProblem}) com valor: "${valorTotalPedido.toFixed(2)}" (regex: ^\\d+\\.\\d{2}$)`);

  console.log("🔧 DEBUG: Custom fields da side conversation:", customFields.map(f => ({ id: f.id, value: f.value })));

  // Determinar o group_id baseado no tipo do ticket
  let targetGroupId = Number(currentUser.groups[0].id); // Grupo padrão
  
  if (formData.tipoDoTicket === "SAC ECOM") {
    targetGroupId = 43506467132308; // Grupo específico para SAC ECOM
  } 
  // 1) CRIAR a side conversation (child) — APENAS { message }
  const createPayload = {
    message: {
      subject: `Novo Pedido - ${pedidoId}`,
      body: messageBody,
      to: [
        {
          support_group_id: targetGroupId,
          support_agent_id: effectiveRequesterId, // Usar o solicitante selecionado
        },
      ],
    },
  };

  const createResp = (await zafClient.request({
    url: `/api/v2/tickets/${ticketId}/side_conversations.json`,
    type: "POST",
    contentType: "application/json",
    data: JSON.stringify(createPayload),
  })) as { side_conversation?: { id: string } };

  const scId = createResp?.side_conversation?.id;
  if (!scId) throw new Error("Side conversation criada sem ID.");

  console.log("🔧 DEBUG: Campos customizados preparados:", customFields.length);
  customFields.forEach((field, index) => {
    console.log(`🔧 DEBUG: Campo ${index + 1}:`, {
      id: field.id,
      value: field.value,
      type: typeof field.value,
    });
  });

  // 2) Obter o ID do child ticket (targetTicketId) com retry curto
  async function getChildIdWithRetry(
    parentId: number,
    sideConvId: string,
    tries = 6,
    delayMs = 600
  ): Promise<number> {
    for (let i = 0; i < tries; i++) {
      const sc = (await zafClient.request({
        url: `/api/v2/tickets/${parentId}/side_conversations/${sideConvId}.json`,
        type: "GET",
        contentType: "application/json",
      })) as {
        side_conversation?: { external_ids?: { targetTicketId?: string } };
      };
      const target = sc?.side_conversation?.external_ids?.targetTicketId;
      if (target) return Number(target);
      await new Promise((r) => setTimeout(r, delayMs));
    }
    throw new Error("Não consegui obter o targetTicketId do child ticket.");
  }
  const childId = await getChildIdWithRetry(ticketId, scId);

  // 3) APLICAR brand/grupo corretos + formulário + campos (no MESMO PUT)
  const updateChildPayload = {
    ticket: {
      type: "problem", // 👈 Define o child ticket como "Problema" na UI
      // >>> MUITO IMPORTANTE: deixar o formulário disponível para esta brand/grupo.
      ...(opts?.brandId ? { brand_id: String(opts.brandId) } : {}),
      ...(opts?.groupIdForChild
        ? { group_id: Number(opts.groupIdForChild) }
        : {}),
      ticket_form_id: String(38483721823252),
      subject: `Novo Pedido - ${pedidoId}`,
      priority: "normal",
      requester_id: effectiveRequesterId, // Definir o requester do child ticket
      // Removido comentário duplicado - já foi criado na side conversation inicial
      custom_fields: customFields.map((cf) => ({
        id: String(cf.id),
        value: cf.value,
      })),
    },
  };

  await zafClient.request({
    url: `/api/v2/tickets/${childId}.json`,
    type: "PUT",
    contentType: "application/json",
    data: JSON.stringify(updateChildPayload),
  });

  // 4) Criar incident (produto) relacionado ao child ticket
  // Buscar produtos selecionados do useNewOrder
  try {
    // O incident será criado posteriormente através de createIncidentFromSideConversation
    // Aqui apenas configuramos o child ticket para receber os incidents
    console.log("✅ DEBUG: Child ticket pronto para receber incidents");
  } catch (incidentError) {
    console.warn(
      "⚠️ Aviso ao preparar child ticket para incidents:",
      incidentError
    );
  }

  // 5) (Opcional) Validar: ler o ticket e garantir que o form aplicou
  const childAfter = (await zafClient.request({
    url: `/api/v2/tickets/${childId}.json`,
    type: "GET",
    contentType: "application/json",
  })) as {
    ticket?: { ticket_form_id?: number; brand_id?: number; group_id?: number };
  };

  console.log("✅ Child form aplicado?:", {
    ticket_form_id: childAfter?.ticket?.ticket_form_id,
    brand_id: childAfter?.ticket?.brand_id,
    group_id: childAfter?.ticket?.group_id,
  });

  // 5) Comentário no pai - REMOVIDO para não criar comentários automáticos
  // await addInternalCommentAboutChildTicket(ticketId, scId);

  return createResp as CreateSideConversationResponse;
}

// Função para construir a mensagem da Side Conversation
function buildSideConversationMessage(
  formData: NewOrderFormData,
  products: SelectedProduct[] = [],
  valorTotalCalculado?: number,
  pedidoId?: string,
  solicitanteNome?: string
): string {
  const fields = [
    // Campos novos da side conversation - usar o nome do solicitante selecionado se disponível
    { label: "Solicitante", value: solicitanteNome || formData.solicitante },
    { label: "Valor do Frete", value: formData.valorFrete },
    { label: "Canal de Venda", value: formData.canalVenda },
    { label: "Tipo do Ticket", value: formData.tipoDoTicket },
    { label: "Categoria do Ticket", value: formData.categoriaTicket },
    { label: "Código de Rastreio", value: formData.codigoRastreio },
    { label: "Erro de Integração", value: formData.errorIntegracao },
    // Usar valor calculado se disponível, senão usar do formData
    {
      label: "Valor do Pedido",
      value:
        valorTotalCalculado !== undefined
          ? `R$ ${valorTotalCalculado.toFixed(2).replace(".", ",")}`
          : formData.valorPedido,
    },

    // Campos existentes
    { label: "Estabelecimento", value: formData.estabelecimento },
    { label: "Tipo de Envio", value: formData.tipoEnvio },
    // Usar pedidoId se fornecido, senão usar formData
    { label: "Pedido Interno", value: pedidoId || formData.pedidoInterno },
    { label: "Classificação da Troca", value: formData.classificacaoTroca },
    { label: "Avaliação de Crédito", value: formData.avaliacaoCredito },
    { label: "Plataforma", value: formData.plataforma },
    { label: "Retira", value: formData.retira },
    { label: "Número de Parcelas", value: formData.numeroParcelas },
    { label: "ECOM PJ", value: formData.ecomPj ? "Sim" : "Não" },
    { label: "Status", value: "Pendente" },
    { label: "Tipo do Registro", value: formData.tipoRegistro },
    { label: "Data de Entrega", value: formData.dataEntrega },
    { label: "Data da Integração", value: formData.dataIntegracao },
    { label: "Observações", value: formData.observacoes },
    { label: "Evidência do Motivo", value: formData.evidenciaMotivo },
  ];

  let message = "**Novo Pedido Criado**\n\n";

  fields.forEach((field) => {
    if (field.value !== undefined && field.value !== "") {
      message += `**${field.label}:** ${field.value}\n`;
    }
  });

  // Adicionar seção de produtos se houver
  if (products && products.length > 0) {
    message += "\n**Produtos Selecionados:**\n";
    products.forEach((product, index) => {
      message += `${index + 1}. **${product.name}**\n`;
      message += `   • Quantidade: ${product.quantidade}\n`;
      message += `   • Valor Unitário: ${product.valorUnitario}\n`;
    });
  }

  if (formData.clonarPedido) {
    message += "\n**⚠️ Clonar Pedido:** Solicitado\n";
  }

  message += `\n---\n*Criado em: ${new Date().toLocaleString("pt-BR")}*`;

  return message;
}

// Função para listar Side Conversations de um ticket
export async function listSideConversations(ticketId: number) {
  try {
    const response = await zafClient.request({
      url: `/api/v2/tickets/${ticketId}/side_conversations`,
      type: "GET",
    });

    return response as { side_conversations: SideConversation[] };
  } catch (error) {
    console.error("Erro ao listar side conversations:", error);
    throw error;
  }
}

// Função para redirecionar para a Side Conversation criada
export function redirectToSideConversation(
  sideConversationId: string,
  ticketId: number
): void {
  try {
    // Construir URL da side conversation no Zendesk
    const zendeskUrl = getZendeskUrl();
    const sideConversationUrl = `${zendeskUrl}/agent/tickets/${ticketId}/side_conversations/${sideConversationId}`;

    console.log(
      "🔗 Redirecionando para Side Conversation:",
      sideConversationUrl
    );

    // Usar o zafClient para navegar para a side conversation
    if (zafClient && typeof zafClient.invoke === "function") {
      // Tentar usar a API do Zendesk para navegação
      zafClient
        .invoke(
          "routeTo",
          "ticket",
          ticketId,
          `side_conversations/${sideConversationId}`
        )
        .catch((error) => {
          console.warn("Erro ao usar routeTo, tentando window.open:", error);
          // Fallback para window.open se routeTo falhar
          window.open(sideConversationUrl, "_blank");
        });
    } else {
      // Fallback final
      window.open(sideConversationUrl, "_blank");
    }
  } catch (error) {
    console.error("Erro ao redirecionar para Side Conversation:", error);
    popupError(
      "Erro ao abrir Side Conversation, mas ela foi criada com sucesso.",
      false
    );
  }
}

// Função alternativa para usar o client do Zendesk para navegação interna
export function navigateToSideConversation(
  sideConversationId: string,
  ticketId: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (!zafClient || typeof zafClient.invoke !== "function") {
        throw new Error("Zendesk client não disponível");
      }

      // Usar a API do ZAF para navegar internamente
      zafClient
        .invoke("routeTo", "ticket", ticketId)
        .then(() => {
          // Pequeno delay para garantir que a página carregou
          setTimeout(() => {
            // Tentar navegar para a side conversation específica
            const sideConvUrl = `/agent/tickets/${ticketId}/side_conversations/${sideConversationId}`;

            // Usar history API se disponível
            if (window.history && window.history.pushState) {
              window.history.pushState(null, "", sideConvUrl);
              resolve();
            } else {
              redirectToSideConversation(sideConversationId, ticketId);
              resolve();
            }
          }, 500);
        })
        .catch((error) => {
          console.warn("Erro no routeTo, usando fallback:", error);
          redirectToSideConversation(sideConversationId, ticketId);
          resolve();
        });
    } catch (error) {
      console.error("Erro na navegação:", error);
      redirectToSideConversation(sideConversationId, ticketId);
      reject(error);
    }
  });
}

// Função mais simples para abrir Side Conversation usando invoke
export async function openSideConversation(
  sideConversationId: string,
  ticketId: number
): Promise<void> {
  try {
    if (!zafClient || typeof zafClient.invoke !== "function") {
      throw new Error("Zendesk client não disponível");
    }

    // Tentar diferentes métodos de navegação do Zendesk
    const methods = [
      // Método 1: Usar routeTo para ir para o ticket e depois para side conversation
      () =>
        zafClient.invoke("routeTo", "ticket", ticketId, {
          sideConversation: sideConversationId,
        }),

      // Método 2: Usar routeTo simples para o ticket
      () =>
        zafClient.invoke("routeTo", "ticket", ticketId).then(() => {
          // Aguardar e tentar focar na side conversation
          setTimeout(() => {
            const event = new CustomEvent("zendesk:showSideConversation", {
              detail: { id: sideConversationId },
            });
            window.dispatchEvent(event);
          }, 1000);
        }),

      // Método 3: Usar URL hash para navegar
      () => {
        window.location.hash = `side_conversation_${sideConversationId}`;
        return Promise.resolve();
      },

      // Método 4: Fallback para abrir em nova aba
      () => {
        redirectToSideConversation(sideConversationId, ticketId);
        return Promise.resolve();
      },
    ];

    // Tentar métodos sequencialmente
    for (let i = 0; i < methods.length; i++) {
      try {
        await methods[i]();
        console.log(
          `✅ Método ${i + 1} funcionou para abrir Side Conversation`
        );
        return;
      } catch (error) {
        console.warn(`⚠️ Método ${i + 1} falhou:`, error);
        if (i === methods.length - 1) {
          throw error;
        }
      }
    }
  } catch (error) {
    console.error(
      "❌ Todos os métodos falharam ao abrir Side Conversation:",
      error
    );
    // Como último recurso, mostrar um popup com informações
    popupSuccess(
      `Side Conversation criada com sucesso! ID: ${sideConversationId}. Verifique a aba "Conversas Laterais" do ticket.`,
      true
    );
    throw error;
  }
}

// Função que usa API oficial do Zendesk para navegação
export async function navigateToTicketSideConversations(
  ticketId: number
): Promise<boolean> {
  try {
    if (!zafClient || typeof zafClient.invoke !== "function") {
      console.warn("Zendesk client não disponível para navegação");
      return false;
    }

    // Usar routeTo que é o método oficial do Zendesk Apps Framework
    await zafClient.invoke("routeTo", "ticket", ticketId);

    // Aguardar um pouco para a página carregar e depois tentar focar nas side conversations
    setTimeout(() => {
      try {
        // Tentar encontrar e clicar na aba de Side Conversations
        const sideConvTab = document.querySelector(
          '[data-test-id="ticket-side-conversations-tab"]'
        ) as HTMLElement;
        if (sideConvTab) {
          sideConvTab.click();
        } else {
          console.log("Aba de Side Conversations não encontrada");
        }
      } catch (domError) {
        console.warn(
          "⚠️ Erro ao tentar ativar aba de Side Conversations:",
          domError
        );
      }
    }, 1500);

    return true;
  } catch (error) {
    console.error("❌ Erro na navegação oficial:", error);
    return false;
  }
}

// Função combinada que tenta todos os métodos
export async function handleSideConversationSuccess(
  sideConversationId: string,
  ticketId: number,
  pedidoInterno?: string
): Promise<void> {
  try {
    // Método 1: Tentar navegação oficial para o ticket
    const navigatedToTicket = await navigateToTicketSideConversations(ticketId);

    if (navigatedToTicket) {
      // Se navegou com sucesso, mostrar info discreta
      popupSuccess(
        'Side Conversation criada! Verificar aba "Conversas Laterais".',
        false
      );
      return;
    }

    // Método 2: Tentar abrir diretamente
    await openSideConversation(sideConversationId, ticketId);
  } catch (error) {
    console.warn(
      "🔄 Métodos de navegação falharam, usando fallback informativo:",
      error
    );

    // Método 3: Fallback informativo
    showSideConversationInfo(sideConversationId, ticketId, pedidoInterno);
  }
}

// Função para obter ID do ticket atual
export async function getCurrentTicketId(): Promise<number> {
  try {
    if (!zafClient || typeof zafClient.get !== "function") {
      throw new Error("Zendesk client não está inicializado");
    }

    const ticketData = await zafClient.get("ticket");

    const ticketId = (ticketData as unknown as { ticket: { id: number } })
      .ticket?.id;

    if (!ticketId) {
      throw new Error("ID do ticket não encontrado na resposta");
    }

    return ticketId;
  } catch (error) {
    console.error("Erro ao obter ID do ticket:", error);
    throw new Error(
      `Falha ao obter ID do ticket: ${
        error instanceof Error ? error.message : "Erro desconhecido"
      }`
    );
  }
}

// Função simples para mostrar informações da Side Conversation criada
export function showSideConversationInfo(
  sideConversationId: string,
  ticketId: number,
  pedidoInterno?: string
): void {
  try {
    // Criar uma mensagem informativa
    const message = `
🎉 Side Conversation criada com sucesso!

📋 **Detalhes:**
• ID da Conversa: ${sideConversationId}
• Ticket: #${ticketId}
${pedidoInterno ? `• Pedido: ${pedidoInterno}` : ""}

✅ **Para visualizar:**
1. Acesse a aba "Conversas Laterais" neste ticket
2. Procure pela conversa com o assunto "Novo Pedido"

🔗 **Link direto:** Ticket #${ticketId} > Side Conversations
    `.trim();

    // Mostrar popup com informações detalhadas
    popupSuccess(message, true);

    // Opcional: Tentar abrir em nova aba como fallback
    const zendeskUrl = getZendeskUrl();
    if (zendeskUrl) {
      // Não abrir automaticamente, mas logar para referência
    }
  } catch (error) {
    console.error(
      "❌ Erro ao mostrar informações da Side Conversation:",
      error
    );
    // Fallback básico
    popupSuccess(`Side Conversation criada! ID: ${sideConversationId}`, false);
  }
}

// Função para criar incident baseado na Side Conversation
export async function createIncidentFromSideConversation(
  sideConversationId: string,
  orderData: NewOrderFormData,
  products: SelectedProduct[],
  parentTicketId: number,
  agentId: number,
  requesterId: number
): Promise<CreateIncidentResponse> {
  try {
    console.log(
      "🔧 DEBUG: Iniciando criação de incidents para side conversation:",
      sideConversationId,
      `- ${products.length} produto(s)`
    );

    // 1) Primeiro, buscar o ID do child ticket a partir da side conversation
    const sideConvDetails = await getSideConversationWithExternalIds(
      parentTicketId,
      sideConversationId
    );
    const childTicketId = sideConvDetails?.external_ids?.targetTicketId;

    if (!childTicketId) {
      throw new Error(
        `Child ticket ID não encontrado para side conversation ${sideConversationId}`
      );
    }

    console.log("✅ DEBUG: Child ticket encontrado:", childTicketId);

    // 2) Criar um incident separado para cada produto
    const { brandIdToUse } = await resolveProblemAndBrand(parentTicketId);

    // Calcular o valor total do pedido baseado nos produtos
    const valorTotalPedido = calculateOrderTotal(products);
    console.log(
      `🔧 DEBUG: Valor total do pedido calculado: R$ ${valorTotalPedido.toFixed(
        2
      )}`
    );

    const incidentResponses: CreateTicketResponse[] = [];

    // Criar um incident para cada produto
    for (let i = 0; i < products.length; i++) {
      const product = products[i];

      console.log(
        `🔧 DEBUG: Criando incident ${i + 1}/${products.length} para produto: ${
          product.name
        }`
      );

      // Construir subject específico para cada produto
      const subject = `Pedido ${orderData.pedidoInterno} - Produto: ${product.name}`;

      // Construir descrição específica para este produto
      const description = `
**Incident criado para o Child Ticket #${childTicketId}**
**Side Conversation: ${sideConversationId}**
**Produto ${i + 1}/${products.length}**

**Dados do Pedido:**
• Estabelecimento: ${orderData.estabelecimento}
• Tipo de Envio: ${orderData.tipoEnvio}
• Pedido Interno: ${orderData.pedidoInterno}
• Classificação de Troca: ${orderData.classificacaoTroca}
• Avaliação de Crédito: ${orderData.avaliacaoCredito}
• Plataforma: ${orderData.plataforma}
• Retira: ${orderData.retira}
• Número de Parcelas: ${orderData.numeroParcelas}
• ECOM PJ: ${orderData.ecomPj ? "Sim" : "Não"}
• Status da Integração: "Pendente"
• Tipo do Registro: ${orderData.tipoRegistro}
• Data de Entrega: ${
        orderData.dataEntrega
          ? formatDateForZendesk(orderData.dataEntrega)
          : "Não informada"
      }
• Data da Integração: ${formatDateForZendesk(orderData.dataIntegracao)}

**Produto:**
• Nome: ${product.name}
• Quantidade: ${product.quantidade}
• Valor Unitário: ${product.valorUnitario}

**Valor Total do Pedido: R$ ${valorTotalPedido.toFixed(2).replace(".", ",")}**

${orderData.observacoes ? `**Observações:**\n${orderData.observacoes}\n` : ""}
${
  orderData.evidenciaMotivo
    ? `**Evidência do Motivo:**\n${orderData.evidenciaMotivo}\n`
    : ""
}

---
*Incident relacionado ao Child Ticket: #${childTicketId}*
*Side Conversation ID: ${sideConversationId}*
*Ticket Principal: #${parentTicketId}*
      `.trim();

      // Custom fields para este produto específico
      const custom_fields: Array<{ id: number; value: unknown }> = [
        // Campo Tipo (sempre "incidente")
        { id: ZENDESK_FIELD_IDS.tipo, value: "incidente" },
      ];

      // Formatar valor total do pedido como string no padrão brasileiro
      const valorPedidoFormatado = String(valorTotalPedido.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
      
      console.log(`🔧 DEBUG: valorPedidoFormatado tipo: ${typeof valorPedidoFormatado}, valor: "${valorPedidoFormatado}"`);
      
      // Adicionar valor do pedido formatado
      custom_fields.push({ id: ZENDESK_FIELD_IDS.valorPedido, value: valorPedidoFormatado });

      // Processar valor do preço para formato brasileiro (regex: ^\d{1,3}(\.\d{3})*(,\d{2})?$)
      let precoValue: string = "";

      if (product.valorUnitario) {
        // Função para formatar preço no padrão brasileiro aceito pelo Zendesk
        const formatarPrecoParaZendesk = (input: string): string => {
          // Remover ruídos (R$, espaços)
          const limpo = input
            .replace(/\s/g, "")
            .replace(/R\$/g, "")
            .replace(/[^\d,.]/g, ""); // Remove tudo exceto dígitos, vírgula e ponto

          let valorNumerico: number;

          // Se tem vírgula como decimal (formato BR: 1.234,56)
          if (limpo.includes(",")) {
            const partes = limpo.split(",");
            if (partes.length === 2) {
              // Remove pontos da parte inteira e reconstrói
              const parteInteira = partes[0].replace(/\./g, "");
              const parteDecimal = partes[1];
              valorNumerico = parseFloat(`${parteInteira}.${parteDecimal}`);
            } else {
              // Só vírgula sem ponto, tratar como decimal
              valorNumerico = parseFloat(limpo.replace(",", "."));
            }
          } else {
            // Sem vírgula, remover todos os pontos e tratar como número inteiro ou decimal com ponto
            if (limpo.split(".").length > 2) {
              // Múltiplos pontos, considerar o último como decimal
              const partes = limpo.split(".");
              const parteDecimal = partes.pop();
              const parteInteira = partes.join("");
              valorNumerico = parseFloat(`${parteInteira}.${parteDecimal}`);
            } else {
              valorNumerico = parseFloat(limpo);
            }
          }

          if (isNaN(valorNumerico)) return "";

          // Formatar no padrão brasileiro: 1.234,56
          return valorNumerico.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };

        precoValue = formatarPrecoParaZendesk(product.valorUnitario);
        console.log(`🔧 DEBUG: Preço formatado para Zendesk (regex ^\\d{1,3}(\\.\\d{3})*(,\\d{2})?$): "${precoValue}" (original: "${product.valorUnitario}")`);
      }

      // Adicionar campos específicos do produto
      custom_fields.push(
        { id: ZENDESK_FIELD_IDS.quantidade, value: product.quantidade },
        { id: ZENDESK_FIELD_IDS.produtoObjeto, value: product.id }
      );

      // Adicionar campo de preço apenas se tiver valor válido
      if (precoValue) {
        custom_fields.push({ id: ZENDESK_FIELD_IDS.preco, value: precoValue });
      }

      console.log(
        `🔧 DEBUG: Campo valorPedido (ID: ${ZENDESK_FIELD_IDS.valorPedido}) sendo definido com valor original: ${valorTotalPedido}`
      );
      
      console.log(
        `🔧 DEBUG: Campo valorPedido formatado como string: "${valorPedidoFormatado}"`
      );

      // Filtrar campos vazios
      const filteredCustomFields = custom_fields.filter(
        (field) => field.value !== undefined && field.value !== ""
      );

      const payload = {
        ticket: {
          subject,
          type: "incident",
          requester_id: requesterId,
          priority: "normal",
          ticket_form_id: 42958690377492, // Form ID para produtos
          problem_id: Number(childTicketId), // 👈 Liga o incident ao problema (child ticket)
          ...(brandIdToUse ? { brand_id: brandIdToUse } : {}),
          // External ID único para cada produto
          external_id: `child_ticket:${childTicketId}:side_conversation:${sideConversationId}:product:${product.id}`,
          comment: {
            body: description,
            public: false,
            author_id: agentId,
          },
          custom_fields: filteredCustomFields,
        },
      };

      console.log(`🔧 DEBUG: Criando incident para produto ${product.name}`);
      console.log(`🔧 DEBUG: Custom fields antes do envio:`, JSON.stringify(filteredCustomFields, null, 2));

      const response = await zafClient.request({
        url: "/api/v2/tickets.json",
        type: "POST",
        contentType: "application/json",
        data: JSON.stringify(payload),
      });

      const incidentResponse = response as CreateTicketResponse;
      const incidentId = incidentResponse.ticket.id;

      incidentResponses.push(incidentResponse);

      console.log(
        `✅ Incident #${incidentId} criado para produto: ${product.name}`
      );
    }

    // Adicionar comentário no child ticket referenciando todos os incidents criados
    try {
      const incidentIds = incidentResponses.map((r) => r.ticket.id).join(", #");

      await zafClient.request({
        url: `/api/v2/tickets/${childTicketId}.json`,
        type: "PUT",
        contentType: "application/json",
        data: JSON.stringify({
          ticket: {
            comment: {
              body: `🔗 **Incidents Relacionados Criados**\n\nIncidents #${incidentIds} foram criados para este pedido.\n\n📋 **Detalhes:**\n• Formulário: 42958690377492 (Itens do pedido)\n• Produtos: ${
                products.length
              } item(s)\n• Incidents criados: ${
                incidentResponses.length
              }\n• Valor total: R$ ${valorTotalPedido
                .toFixed(2)
                .replace(".", ",")}\n• Data: ${new Date().toLocaleString(
                "pt-BR"
              )}\n\n---\n*Comentário automático do sistema*`,
              public: false,
              type: "Comment",
            },
          },
        }),
      });

      console.log(
        `✅ Comentário adicionado ao child ticket #${childTicketId} sobre ${incidentResponses.length} incidents criados`
      );
    } catch (commentError) {
      console.warn(
        "⚠️ Erro ao adicionar comentário no child ticket:",
        commentError
      );
    }

    // Retornar o primeiro incident como resposta principal com o childTicketId
    const response = incidentResponses[0];
    return {
      ...response,
      childTicketId: Number(childTicketId)
    } as CreateIncidentResponse;
  } catch (error) {
    console.error("❌ Erro ao criar incident:", error);
    throw error;
  }
}

/**
 * Calcula o valor total do pedido baseado nos produtos selecionados
 */
function calculateOrderTotal(products: SelectedProduct[]): number {
  console.log(
    "🔧 DEBUG: Calculando valor total do pedido para produtos:",
    products
  );

  const total = products.reduce((total, product) => {
    // Processar valor unitário para garantir formato correto
    let unitPrice: number = 0;

    console.log(
      `🔧 DEBUG: Processando produto ${product.name}: valorUnitario="${product.valorUnitario}", quantidade=${product.quantidade}`
    );

    if (typeof product.valorUnitario === "string") {
      // Verificar se o valor está vazio ou é apenas zeros
      if (!product.valorUnitario || product.valorUnitario.trim() === "" || product.valorUnitario.trim() === "0") {
        unitPrice = 0;
        console.log(`🔧 DEBUG: Valor unitário vazio ou zero, usando 0`);
      } else {
        // Remover símbolos de moeda e espaços
        let cleanPrice = product.valorUnitario.replace(/[R$\s]/g, "");
        
        console.log(`🔧 DEBUG: Antes da limpeza: "${product.valorUnitario}"`);
        console.log(`🔧 DEBUG: Depois de remover R$ e espaços: "${cleanPrice}"`);
        
        // Priorizar formato com ponto decimal (100.00)
        // Mas ainda suportar vírgula por compatibilidade
        if (cleanPrice.includes(",") && !cleanPrice.includes(".")) {
          // Apenas vírgula: converter para ponto (22,5 -> 22.5)
          cleanPrice = cleanPrice.replace(",", ".");
          console.log(`🔧 DEBUG: Vírgula convertida para ponto: "${cleanPrice}"`);
        }
        else if (cleanPrice.includes(",") && cleanPrice.includes(".")) {
          // Ambos: formato brasileiro 1.000,50 -> 1000.50
          cleanPrice = cleanPrice.replace(/\./g, "").replace(",", ".");
          console.log(`🔧 DEBUG: Formato brasileiro convertido: "${cleanPrice}"`);
        }
        // Se tem apenas ponto, mantém como está (formato preferido: 100.00)

        unitPrice = parseFloat(cleanPrice) || 0;
        console.log(
          `🔧 DEBUG: Valor unitário convertido: "${product.valorUnitario}" -> "${cleanPrice}" -> ${unitPrice}`
        );
      }
    } else if (typeof product.valorUnitario === "number") {
      unitPrice = product.valorUnitario;
    } else {
      // Valor undefined ou null
      unitPrice = 0;
      console.log(`🔧 DEBUG: valorUnitario é ${typeof product.valorUnitario}, usando 0`);
    }

    const productTotal = unitPrice * product.quantidade;
    console.log(
      `🔧 DEBUG: Valor do produto: ${unitPrice} x ${product.quantidade} = ${productTotal}`
    );

    return total + productTotal;
  }, 0);

  console.log(`🔧 DEBUG: Valor total calculado: ${total}`);
  return total;
}

// Função para buscar incidents relacionados a um ticket usando múltiplas abordagens
// Função removida - lógica incorporada em getIncidentsBySideConversation

// Função aprimorada para buscar incidents relacionados a um side conversation específica
export async function getIncidentsBySideConversation(
  ticketId: number,
  sideConversationId: string
): Promise<unknown[]> {
  try {
    console.log(
      `🔧 DEBUG: Buscando incidents para side conversation: ${sideConversationId}`
    );

    // PASSO 1: Buscar detalhes da side conversation para pegar o child ticket ID (problem)
    const sideConversationDetails = await getSideConversationWithExternalIds(
      ticketId,
      sideConversationId
    );

    if (sideConversationDetails?.external_ids?.targetTicketId) {
      const problemId = Number(sideConversationDetails.external_ids.targetTicketId);
      console.log(`✅ Problem ID encontrado: ${problemId} para side conversation ${sideConversationId}`);

      // PASSO 2: Buscar incidents que tenham este problem_id
      try {
        console.log(`� Buscando incidents com problem_id: ${problemId}`);
        
        // Primeira tentativa: busca por fieldvalue
        const response1 = (await zafClient.request({
          url: `/api/v2/search.json?query=fieldvalue:problem_id:${problemId}`,
          type: "GET",
        })) as { results?: unknown[] };

        if (response1.results && response1.results.length > 0) {
          console.log(`✅ Encontrados ${response1.results.length} incidents via fieldvalue:problem_id`);
          return response1.results;
        }

        // Segunda tentativa: busca simples por problem_id
        const response2 = (await zafClient.request({
          url: `/api/v2/search.json?query=problem_id:${problemId}`,
          type: "GET",
        })) as { results?: unknown[] };

        if (response2.results && response2.results.length > 0) {
          console.log(`✅ Encontrados ${response2.results.length} incidents via problem_id`);
          return response2.results;
        }

        // Terceira tentativa: buscar todos os tickets recentes e filtrar
        const response3 = (await zafClient.request({
          url: `/api/v2/tickets.json?sort_by=created_at&sort_order=desc&per_page=100`,
          type: "GET",
        })) as { tickets?: Array<{ problem_id?: number; id: number; subject?: string; type?: string }> };

        if (response3.tickets) {
          const incidents = response3.tickets.filter(
            (ticket) => ticket.problem_id === problemId && ticket.type === 'incident'
          );
          
          if (incidents.length > 0) {
            console.log(`✅ Encontrados ${incidents.length} incidents via filtro manual`);
            return incidents;
          }
        }

      } catch (error) {
        console.warn("Erro na busca por problem_id:", error);
      }
    }

    // FALLBACK 1: Buscar por external_id (método antigo)
    console.log("🔧 DEBUG: Tentando fallback por external_id...");
    try {
      const response = (await zafClient.request({
        url: `/api/v2/search.json?query=external_id:*side_conversation\\:${sideConversationId}*`,
        type: "GET",
      })) as { results?: unknown[] };

      if (response.results && response.results.length > 0) {
        console.log(`✅ Encontrados ${response.results.length} incidents via external_id`);
        return response.results;
      }
    } catch (error) {
      console.warn("Busca por external_id falhou:", error);
    }

    // FALLBACK 2: Retornar o próprio problem ticket se não encontrou incidents
    if (sideConversationDetails?.external_ids?.targetTicketId) {
      try {
        console.log("🔧 DEBUG: Retornando o problem ticket como produto...");
        const problemTicketResponse = (await zafClient.request({
          url: `/api/v2/tickets/${sideConversationDetails.external_ids.targetTicketId}.json`,
          type: "GET",
        })) as { ticket?: unknown };

        if (problemTicketResponse.ticket) {
          console.log("✅ Retornando problem ticket como produto");
          return [problemTicketResponse.ticket];
        }
      } catch (error) {
        console.warn("Erro ao buscar problem ticket:", error);
      }
    }

    console.log("⚠️ Nenhum produto encontrado para esta side conversation");
    return [];
  } catch (generalError) {
    console.error(
      "❌ Erro geral ao buscar produtos da side conversation:",
      generalError
    );
    return [];
  }
}

// Função para buscar detalhes completos de um incident (incluindo custom fields)
export async function getIncidentDetails(incidentId: number): Promise<unknown> {
  try {
    const response = (await zafClient.request({
      url: `/api/v2/tickets/${incidentId}.json?include=custom_fields`,
      type: "GET",
    })) as { ticket?: unknown };

    return response.ticket;
  } catch (error) {
    console.error(
      `❌ Erro ao buscar detalhes do incident #${incidentId}:`,
      error
    );
    return null;
  }
}

// Função para fechar uma side conversation (seguindo a documentação oficial)
export async function deleteSideConversation(
  ticketId: number,
  sideConversationId: string
): Promise<boolean> {
  try {
    // Seguir exatamente a documentação oficial do Zendesk
    // PUT /api/v2/tickets/{ticket_id}/side_conversations/{side_conversation_id}.json
    await zafClient.request({
      url: `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}.json`,
      type: "PUT",
      contentType: "application/json",
      data: JSON.stringify({
        side_conversation: {
          state: "closed",
        },
      }),
    });

    return true;
  } catch (error) {
    console.error(
      `❌ Erro ao fechar side conversation ${sideConversationId}:`,
      error
    );
    throw error;
  }
}

// Função para atualizar campos customizados do ticket com os dados do pedido
export async function updateTicketCustomFields(
  ticketId: number,
  formData: NewOrderFormData
): Promise<void> {
  try {
    const customFields = [
      {
        id: ZENDESK_FIELD_IDS.estabelecimento,
        value: formData.estabelecimento,
      },
      { id: ZENDESK_FIELD_IDS.tipoEnvio, value: formData.tipoEnvio },
      { id: ZENDESK_FIELD_IDS.pedidoInterno1, value: formData.pedidoInterno },
      { id: ZENDESK_FIELD_IDS.pedidoInterno2, value: formData.pedidoInterno },
      {
        id: ZENDESK_FIELD_IDS.classificacaoTroca,
        value: formData.classificacaoTroca,
      },
      {
        id: ZENDESK_FIELD_IDS.avaliacaoCredito,
        value: formData.avaliacaoCredito,
      },
      { id: ZENDESK_FIELD_IDS.plataforma, value: formData.plataforma },
      { id: ZENDESK_FIELD_IDS.retira, value: formData.retira },
      { id: ZENDESK_FIELD_IDS.numeroParcelas, value: formData.numeroParcelas },
      { id: ZENDESK_FIELD_IDS.ecomPj, value: formData.ecomPj },
      { id: ZENDESK_FIELD_IDS.observacoes, value: formData.observacoes },
      {
        id: ZENDESK_FIELD_IDS.evidenciaMotivo,
        value: formData.evidenciaMotivo,
      },
      {
        id: ZENDESK_FIELD_IDS.statusIntegracao,
        value: "pedido_status_da_integracao_pendente",
      },
      {
        id: ZENDESK_FIELD_IDS.statusIntegracaoTwo,
        value:  "pendente_implantação",
      },
      { id: ZENDESK_FIELD_IDS.tipoRegistro, value: formData.tipoRegistro },
      { id: ZENDESK_FIELD_IDS.dataEntrega, value: formData.dataEntrega },
      { id: ZENDESK_FIELD_IDS.dataIntegracao, value: formData.dataIntegracao },
      { id: ZENDESK_FIELD_IDS.clonarPedido, value: formData.clonarPedido },
    ].filter((field) => field.value !== undefined && field.value !== "");

    await zafClient.request({
      url: `/api/v2/tickets/${ticketId}`,
      type: "PUT",
      contentType: "application/json",
      data: JSON.stringify({
        ticket: {
          custom_fields: customFields,
        },
      }),
    });

    popupSuccess("Campos do ticket atualizados com sucesso!", false);
  } catch (error) {
    console.error("Erro ao atualizar campos do ticket:", error);
    popupError("Erro ao atualizar campos do ticket.", false);
    throw error;
  }
}

// Função para buscar usuários do Zendesk
export async function searchUsers(term: string): Promise<unknown[]> {
  try {
    if (!term || term.trim().length < 2) {
      console.log("🔍 DEBUG: Termo muito curto, retornando array vazio");
      return [];
    }

    console.log(`🔍 DEBUG: Buscando usuários com termo: "${term}"`);
    console.log("🔍 DEBUG: zafClient disponível:", !!zafClient);
    console.log("🔍 DEBUG: zafClient.request disponível:", typeof zafClient?.request);

    if (!zafClient) {
      console.error("❌ DEBUG: zafClient não está disponível");
      return [];
    }

    // Tentar uma abordagem diferente para a busca
    const trimmedTerm = term.trim();
    let url = `/api/v2/search.json?query=type:user ${encodeURIComponent(trimmedTerm)}`;
    
    console.log("🔍 DEBUG: URL da busca:", url);

    let response;
    try {
      response = (await zafClient.request({
        url,
        type: "GET",
      })) as { results?: unknown[]; count?: number };
    } catch (searchError) {
      console.error("❌ DEBUG: Erro na primeira tentativa de busca:", searchError);
      
      // Tentar busca alternativa com query mais simples
      url = `/api/v2/users/search.json?query=${encodeURIComponent(trimmedTerm)}`;
      console.log("🔍 DEBUG: Tentando URL alternativa:", url);
      
      try {
        response = (await zafClient.request({
          url,
          type: "GET",
        })) as { users?: unknown[]; count?: number };
        
        // Normalizar resposta da API alternativa
        if (response && 'users' in response) {
          response = { results: response.users, count: response.count };
        }
      } catch (altSearchError) {
        console.error("❌ DEBUG: Erro na busca alternativa:", altSearchError);
        throw altSearchError;
      }
    }

    console.log("🔍 DEBUG: Resposta completa da API:", response);

    const users = (response as { results?: unknown[] }).results || [];
    console.log(`✅ DEBUG: Encontrados ${users.length} usuários`);
    
    if (users.length > 0) {
      console.log("🔍 DEBUG: Primeiro usuário encontrado:", users[0]);
    }

    return users;
  } catch (error) {
    console.error("❌ Erro ao buscar usuários:", error);
    console.error("❌ Detalhes do erro:", {
      message: error instanceof Error ? error.message : 'Erro desconhecido',
      stack: error instanceof Error ? error.stack : undefined,
      response: (error as unknown as { response?: unknown })?.response
    });
    return [];
  }
}

// Mapeamento de formulários por marca
const BRAND_FORM_MAPPING = {
  "SAC AT": {
    formId: 37682384531348,
    categoriaFieldId: 37665983385876
  },
  "SAC ECOM": {
    formId: 37682358343316,
    categoriaFieldId: 37667492934292
  },
  "JURIDICO": {
    formId: 38314764405140,
    categoriaFieldId: 37665002261652
  }
} as const;

// Função para obter o valor da categoria baseado na marca atual
export async function getCategoriaValueFromCurrentTicket(brandName?: string): Promise<string | null> {
  try {
    if (!brandName) {
      console.log("🔧 DEBUG: Brand não fornecido, retornando null");
      return null;
    }

    const normalizedBrandName = brandName.toUpperCase().trim();
    let mapping = BRAND_FORM_MAPPING[normalizedBrandName as keyof typeof BRAND_FORM_MAPPING];
    
    // Fallback para variações de nome
    if (!mapping && normalizedBrandName.includes("ECOM")) {
      mapping = BRAND_FORM_MAPPING["SAC ECOM"];
    } else if (!mapping && (normalizedBrandName.includes("JURÍDICO") || normalizedBrandName.includes("JURIDICO"))) {
      mapping = BRAND_FORM_MAPPING["JURIDICO"];
    }

    if (!mapping) {
      console.log(`🔧 DEBUG: Mapeamento não encontrado para brand: ${normalizedBrandName}`);
      return null;
    }

    console.log(`🔧 DEBUG: Obtendo categoria para brand ${brandName} do campo ${mapping.categoriaFieldId}`);

    // Obter o valor do campo personalizado do ticket atual
    const fieldValue = await getTicketFieldValue(mapping.categoriaFieldId);
    
    console.log(`🔧 DEBUG: Valor obtido do campo ${mapping.categoriaFieldId}:`, fieldValue);

    // Garantir que retornamos uma string ou null
    if (fieldValue && typeof fieldValue === 'string') {
      return fieldValue;
    } else if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
      return String((fieldValue as { value: unknown }).value) || null;
    }

    return null;
  } catch (error) {
    console.error("❌ Erro ao obter categoria do ticket atual:", error);
    return null;
  }
}

// Função de teste para verificar se a API do Zendesk está funcionando
export async function testZendeskApi(): Promise<void> {
  try {
    console.log("🔧 DEBUG: Testando conectividade com API do Zendesk...");
    
    if (!zafClient) {
      console.error("❌ DEBUG: zafClient não disponível para teste");
      return;
    }

    // Testar endpoint básico
    const basicTest = await zafClient.request({
      url: '/api/v2/users/me.json',
      type: 'GET',
    });
    console.log("✅ DEBUG: Teste básico da API bem-sucedido:", basicTest);
    
    // Testar busca simples
    const searchTest = await zafClient.request({
      url: '/api/v2/search.json?query=type:user',
      type: 'GET',
    });
    console.log("✅ DEBUG: Teste de busca básica bem-sucedido:", searchTest);
    
  } catch (error) {
    console.error("❌ DEBUG: Falha no teste da API:", error);
  }
}
